import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Plus, Minus, Trash2, CheckCircle2, Search, ShoppingCart, ArrowDownLeft, ArrowLeft, Edit2 } from 'lucide-react';
import CustomDatePicker from '../../shared/CustomDatePicker';
import { formatWarehouseDate } from '../../../../../utils/dateUtils';
import { SEED_PRODUCTS } from '../../../../../services/api/warehouseApi';
import { getAllProducts, getProductCategories } from '../../../../../services/api/productApi';
import { useAuth } from '../../../../../context/AuthContext';
import ProductAvatar from '../../../../../components/common/ProductAvatar';

const RETURN_REASONS = [
    'Slow Moving',
    'Damaged',
    'Expired',
    'Near Expiry',
    'Wrong Item Loaded',
    'Warehouse Request',
    'Seasonal Rotation',
    'Other'
];

const formatQtyDisplay = (val) => {
    const num = Number(val) || 0;
    if (num >= 1e15) return `${(num / 1e15).toFixed(2)}Q`;
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    return num.toLocaleString();
};

const CreateOffloadRequestModal = ({ isOpen, onClose, onSubmitRequest, initialData = null }) => {
    const { user } = useAuth() || {};
    const loggedSalesperson = user?.name || user?.username || 'Salesperson';
    const userVehicle = user?.vehicleNumber || user?.vehicle || user?.assignedVehicle || user?.assignedSalesPersonVehicleNumber || 'Van 01';

    // Header State
    const [requestDate, setRequestDate] = useState(initialData?.requestDate || new Date().toISOString().split('T')[0]);
    const [salesperson] = useState(initialData?.salesperson || loggedSalesperson);
    const [vehicle, setVehicle] = useState(initialData?.vehicle || userVehicle);
    const [warehouse, setWarehouse] = useState(initialData?.warehouse || 'Main Warehouse');
    const [remarks, setRemarks] = useState(initialData?.remarks || '');
    const [otherReasonRemarks, setOtherReasonRemarks] = useState('');
    const [defaultReason, setDefaultReason] = useState('Slow Moving');
    const [submittedReqNo, setSubmittedReqNo] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isReviewing, setIsReviewing] = useState(false);

    // Selected Items State
    const [items, setItems] = useState(() => initialData?.items || []);

    // Catalog & Filter States (POS Enterprise Pagination Pattern)
    const PAGE_SIZE = 60;
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedOnly, setSelectedOnly] = useState(false);

    const [productPage, setProductPage] = useState(1);
    const [productTotalPages, setProductTotalPages] = useState(1);
    const [productTotalElements, setProductTotalElements] = useState(0);

    const searchDebounceRef = useRef(null);
    const productsLoadingRef = useRef(false);
    const isFetchingRef = useRef(false);
    const lastRequestedPageRef = useRef(1);
    const scrollContainerRef = useRef(null);
    const sentinelRef = useRef(null);

    // Fetch Products (Server-side Paginated)
    const fetchProducts = useCallback(async (pageNum, search, category, append = false) => {
        if (isFetchingRef.current && append) return;
        isFetchingRef.current = true;
        setLoadingProducts(true);
        productsLoadingRef.current = true;
        try {
            const [prodRes, catRes] = await Promise.all([
                getAllProducts({
                    page: pageNum - 1, // 0-indexed in backend
                    size: PAGE_SIZE,
                    search: search?.trim() || undefined,
                    category: category || undefined,
                    status: 'ACTIVE'
                }),
                category ? Promise.resolve([]) : getProductCategories()
            ]);

            const fetchedProds = (prodRes.content || prodRes.products || prodRes || []).map(p => ({
                id: p.id || p.publicId,
                name: p.name,
                unit: p.unit || p.uom || 'Pcs',
                stock: p.stock || p.availableQuantity || 0,
                category: p.category || 'General',
                image: p.image || p.imageUrl || null,
                sku: p.sku || ''
            }));

            if (append) {
                setProducts(prev => {
                    const existingIds = new Set(prev.map(p => p.id));
                    const newItems = fetchedProds.filter(p => !existingIds.has(p.id));
                    return [...prev, ...newItems];
                });
            } else {
                setProducts(fetchedProds.length > 0 ? fetchedProds : SEED_PRODUCTS);
            }

            setProductTotalPages(prodRes.totalPages || 1);
            setProductTotalElements(prodRes.totalElements || (fetchedProds.length || 0));

            if (!category && Array.isArray(catRes) && catRes.length > 0) {
                setCategories(catRes);
            }
        } catch (e) {
            if (!append) {
                setProducts(SEED_PRODUCTS);
            }
        } finally {
            setLoadingProducts(false);
            productsLoadingRef.current = false;
            isFetchingRef.current = false;
        }
    }, []);

    // Reset filters and search term whenever modal opens
    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSelectedCategory('');
            setSelectedOnly(false);
            setProductPage(1);
            setIsReviewing(false);
            setIsSuccess(false);
            setRemarks('');
            setOtherReasonRemarks('');
            if (initialData?.items) {
                setItems(initialData.items);
            } else {
                setItems([]);
            }
        }
    }, [isOpen, initialData]);

    // Initial load & debounced search/category filter change (resets to page 1)
    useEffect(() => {
        if (!isOpen) return;

        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setProductPage(1);
            lastRequestedPageRef.current = 1;
            fetchProducts(1, searchTerm, selectedCategory, false);
        }, searchTerm ? 300 : 0);

        return () => clearTimeout(searchDebounceRef.current);
    }, [isOpen, searchTerm, selectedCategory, fetchProducts]);

    // Fetch page N when page increments via infinite scroll (productPage > 1)
    useEffect(() => {
        if (!isOpen || productPage <= 1) return;
        fetchProducts(productPage, searchTerm, selectedCategory, true);
    }, [isOpen, productPage, fetchProducts]);

    // IntersectionObserver for pre-fetching next pages before reaching the bottom
    useEffect(() => {
        if (!isOpen || selectedOnly || loadingProducts || productPage >= productTotalPages) return;
        const sentinel = sentinelRef.current;
        const container = scrollContainerRef.current;
        if (!sentinel || !container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    if (isFetchingRef.current) return;
                    if (lastRequestedPageRef.current >= productTotalPages) return;
                    lastRequestedPageRef.current = productPage + 1;
                    setProductPage(prev => prev + 1);
                }
            },
            {
                root: container,
                rootMargin: '250px', // Pre-fetch 250px before end of scroll
                threshold: 0.01
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isOpen, selectedOnly, loadingProducts, productPage, productTotalPages]);

    // Passive scroll listener fallback
    const handleScroll = (e) => {
        if (selectedOnly || productsLoadingRef.current || isFetchingRef.current || productPage >= productTotalPages) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 150) {
            if (lastRequestedPageRef.current > productPage) return;
            lastRequestedPageRef.current = productPage + 1;
            setProductPage(prev => prev + 1);
        }
    };

    // Populate initial data when editing
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setItems(initialData.items || []);
                setRequestDate(initialData.requestDate || new Date().toISOString().split('T')[0]);
                setVehicle(initialData.vehicle || userVehicle);
                setWarehouse(initialData.warehouse || 'Main Warehouse');
                setRemarks(initialData.remarks || '');
            } else {
                setItems([]);
                setRequestDate(new Date().toISOString().split('T')[0]);
                setVehicle(userVehicle);
                setWarehouse('Main Warehouse');
                setRemarks('');
            }
            setIsSuccess(false);
            setIsReviewing(false);
        }
    }, [initialData, userVehicle, isOpen]);

    // Server-side paginated products are already filtered by search & category.
    // Client-side filtering only applies when "Selected" filter pill is active.
    const displayedProducts = useMemo(() => {
        if (selectedOnly) {
            const selectedProductIds = new Set(items.map(i => i.productId || i.id));
            return products.filter(p => selectedProductIds.has(p.id));
        }
        return products;
    }, [products, items, selectedOnly]);

    if (!isOpen) return null;

    // Helper to get item for a product ID
    const getItemForProduct = (productId) => {
        return items.find(i => (i.productId || i.id) === productId);
    };

    // Update quantity for a product
    const setProductQty = (product, newQty, reasonVal = null) => {
        const pId = product.id || product.productId;
        const pName = product.name || product.productName;
        const pUnit = product.unit || product.uom || 'Pcs';
        const pImage = product.image || null;

        setItems(prev => {
            const existingIndex = prev.findIndex(i => (i.productId || i.id) === pId);
            if (newQty <= 0) {
                return prev.filter(i => (i.productId || i.id) !== pId);
            }
            if (existingIndex > -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    qty: newQty,
                    reason: reasonVal || updated[existingIndex].reason || defaultReason
                };
                return updated;
            } else {
                return [
                    ...prev,
                    {
                        id: `oi_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                        productId: pId,
                        productName: pName,
                        qty: newQty,
                        unit: pUnit,
                        image: pImage,
                        reason: reasonVal || defaultReason
                    }
                ];
            }
        });
    };

    const handleItemReasonChange = (product, newReason) => {
        const existingItem = getItemForProduct(product.id || product.productId);
        const currentQty = existingItem ? Number(existingItem.qty) || 1 : 1;
        setProductQty(product, currentQty, newReason);
    };

    const handleIncrement = (product) => {
        const existingItem = getItemForProduct(product.id || product.productId);
        const currentQty = existingItem ? Number(existingItem.qty) || 0 : 0;
        setProductQty(product, currentQty + 1);
    };

    const handleDecrement = (product) => {
        const existingItem = getItemForProduct(product.id || product.productId);
        const currentQty = existingItem ? Number(existingItem.qty) || 0 : 0;
        if (currentQty > 0) {
            setProductQty(product, currentQty - 1);
        }
    };

    const handleQtyInputChange = (product, value) => {
        if (value === '') {
            setProductQty(product, 0);
            return;
        }
        const parsed = parseInt(value, 10);
        if (!isNaN(parsed) && parsed >= 0) {
            setProductQty(product, parsed);
        }
    };

    const handleClearAll = () => {
        setItems([]);
    };

    const isOtherSelected = items.some(i => i.reason === 'Other');
    const totalProductsCount = items.length;
    const totalQuantitySum = items.reduce((sum, i) => sum + (Number(i.qty) || 0), 0);

    const handleProceedToReview = () => {
        if (items.length === 0) {
            alert('Please select at least one item to offload.');
            return;
        }

        if (isOtherSelected && !otherReasonRemarks && !remarks) {
            alert('Remarks are mandatory when "Other" return reason is selected.');
            return;
        }

        setIsReviewing(true);
    };

    const handleFinalSubmit = async () => {
        if (items.length === 0) {
            alert('Please select at least one product to offload.');
            return;
        }

        if (isOtherSelected && !otherReasonRemarks && !remarks) {
            alert('Remarks are mandatory when "Other" return reason is selected.');
            return;
        }

        const result = await onSubmitRequest({
            id: initialData?.id,
            requestDate,
            salesperson,
            vehicle: vehicle || userVehicle,
            warehouse: warehouse || 'Main Warehouse',
            remarks: isOtherSelected && otherReasonRemarks ? `${remarks} (Other Reason: ${otherReasonRemarks})` : remarks,
            items,
            status: initialData?.status || 'Submitted'
        });

        const realReqNo = result?.requestNo || initialData?.requestNo || 'OR-00001';
        setSubmittedReqNo(realReqNo);
        setIsSuccess(true);
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-4xl flex flex-col max-h-[92vh] overflow-hidden my-auto">

                {/* POS Header */}
                <div className="p-3.5 sm:p-5 bg-white dark:bg-slate-800 border-b border-slate-200/80 dark:border-slate-700 flex items-center justify-between shrink-0 shadow-xs">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                            <ArrowDownLeft className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-base sm:text-lg font-black text-slate-800 dark:text-white leading-tight flex items-center gap-2">
                                {isReviewing ? 'Review Offload Request' : (initialData ? 'Edit Offload Request' : 'New Offload Request')}
                            </h2>
                            <p className="text-[11px] sm:text-xs text-slate-400 font-medium mt-0.5">
                                {isReviewing ? 'Confirm offload return items before submission' : 'Select stock items to return to warehouse'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body Content */}
                {isSuccess || isReviewing ? (
                    <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 space-y-4 custom-scrollbar-blue">
                        {isSuccess ? (
                            /* SUCCESS VIEW */
                            <div className="py-10 text-center space-y-5 animate-in fade-in zoom-in duration-200 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
                                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-sm">
                                    <CheckCircle2 className="w-10 h-10" />
                                </div>

                                <div>
                                    <h3 className="text-xl font-black text-slate-800 dark:text-white">Offload Request Submitted</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        Your offload request has been submitted successfully for warehouse verification.
                                    </p>
                                </div>

                                <div className="max-w-md mx-auto p-4 bg-slate-50 dark:bg-slate-700/40 rounded-2xl border border-slate-200/80 dark:border-slate-700 text-left text-xs space-y-2.5">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 font-medium">Request No</span>
                                        <span className="font-bold text-blue-600 dark:text-blue-400">{submittedReqNo}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 font-medium">Status</span>
                                        <span className="font-semibold text-blue-600 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded-full text-[11px]">Pending Verification</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 font-medium">Offload Date</span>
                                        <span className="font-medium text-slate-800 dark:text-white">{formatWarehouseDate(requestDate)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500 font-medium">Total Items</span>
                                        <span className="font-bold text-slate-900 dark:text-white">{totalQuantitySum} pcs ({totalProductsCount} Items)</span>
                                    </div>
                                </div>

                                <div className="flex items-center justify-center gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-5 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-800 dark:text-white rounded-xl text-xs font-semibold transition-all"
                                    >
                                        Close
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsSuccess(false);
                                            setIsReviewing(false);
                                            setItems([]);
                                            setRemarks('');
                                            setOtherReasonRemarks('');
                                            setSearchTerm('');
                                            setSelectedCategory('');
                                            setSelectedOnly(false);
                                            setProductPage(1);
                                        }}
                                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 active:scale-95 transition-all"
                                    >
                                        Create Another Request
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* REVIEW VIEW BEFORE SUBMISSION */
                            <div className="space-y-4 animate-in fade-in duration-150">
                                {/* Summary Card */}
                                <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-2xs space-y-3">
                                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-2.5">
                                        <h3 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-wider">
                                            Offload Summary
                                        </h3>
                                        <button
                                            type="button"
                                            onClick={() => setIsReviewing(false)}
                                            className="text-xs text-blue-600 dark:text-blue-400 font-bold hover:underline flex items-center gap-1"
                                        >
                                            <Edit2 className="w-3.5 h-3.5" /> Edit Items
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                                        <div>
                                            <span className="text-[10px] uppercase font-bold text-slate-400 block">Offload Date</span>
                                            <span className="font-bold text-slate-800 dark:text-white">{formatWarehouseDate(requestDate)}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-bold text-slate-400 block">Salesperson</span>
                                            <span className="font-bold text-slate-800 dark:text-white">{salesperson}</span>
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <span className="text-[10px] uppercase font-bold text-slate-400 block">Remarks</span>
                                            <span className="font-semibold text-slate-600 dark:text-slate-300 italic">
                                                {remarks || 'No remarks provided'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Selected Items Review List */}
                                <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-2xs space-y-3">
                                    <div className="flex items-center justify-between text-xs font-black text-slate-800 dark:text-white px-1">
                                        <span>Return Items ({totalProductsCount})</span>
                                        <span className="text-blue-600 dark:text-blue-400">{totalQuantitySum} Total Units</span>
                                    </div>

                                    <div className="divide-y divide-slate-100 dark:divide-slate-700/60 max-h-72 overflow-y-auto custom-scrollbar-blue pr-1">
                                        {items.map((item, index) => (
                                            <div key={item.id} className="py-2.5 flex items-center justify-between gap-3 text-xs">
                                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                    <span className="w-5 h-5 rounded-md bg-blue-50 dark:bg-slate-700 text-blue-600 dark:text-blue-400 text-[11px] font-black flex items-center justify-center shrink-0">
                                                        {index + 1}
                                                    </span>
                                                    <div className="min-w-0">
                                                        <p className="font-bold text-slate-800 dark:text-white truncate">
                                                            {item.productName || item.name}
                                                        </p>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className="text-[10px] font-semibold text-slate-400">
                                                                Unit: {item.unit || 'PCS'}
                                                            </span>
                                                            <span className="text-[10px] font-bold text-blue-600 bg-blue-50 dark:bg-blue-900/40 px-1.5 py-0.5 rounded">
                                                                Reason: {item.reason || defaultReason}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3 shrink-0">
                                                    <span className="font-black text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-xl">
                                                        {item.qty} {item.unit || 'PCS'}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* MAIN POS CATALOG SELECTION VIEW - FIXED TOP HEADER + SCROLLABLE PRODUCTS LIST */
                    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                        {/* Fixed Top Filter & Metadata Section */}
                        <div className="shrink-0 p-3 sm:p-4 bg-slate-50 dark:bg-slate-900 border-b border-slate-200/80 dark:border-slate-800 space-y-2.5 shadow-2xs">
                            {/* Metadata Form Header Card */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5 sm:gap-3 bg-white dark:bg-slate-800 p-3 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-2xs">
                                <div>
                                    <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Offload Date</label>
                                    <CustomDatePicker
                                        mode="single"
                                        value={requestDate}
                                        onChange={(val) => setRequestDate(val)}
                                        quickFilters={false}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Salesperson</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={salesperson}
                                        className="w-full px-3 py-1.5 sm:py-2 text-xs bg-slate-100 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-500 dark:text-slate-400 font-semibold cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Default Reason</label>
                                    <select
                                        value={defaultReason}
                                        onChange={(e) => setDefaultReason(e.target.value)}
                                        className="w-full px-3 py-1.5 sm:py-2 text-xs font-semibold bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white outline-none cursor-pointer"
                                    >
                                        {RETURN_REASONS.map(r => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Remarks (Optional)</label>
                                    <input
                                        type="text"
                                        placeholder="Enter offload notes..."
                                        value={remarks}
                                        onChange={(e) => setRemarks(e.target.value)}
                                        className="w-full px-3 py-1.5 sm:py-2 text-xs bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white outline-none"
                                    />
                                </div>
                            </div>

                            {/* POS Search & Filter Navigation Bar */}
                            <div className="bg-white dark:bg-slate-800 p-3 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-2xs space-y-2">
                                <div className="flex gap-2 items-center">
                                    {/* Search Input */}
                                    <div className="relative flex-1 min-w-0">
                                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input
                                            type="text"
                                            placeholder="Search products to offload..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-9 pr-8 py-2 text-xs font-medium bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white outline-none"
                                        />
                                        {searchTerm && (
                                            <button
                                                onClick={() => setSearchTerm('')}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Category Dropdown */}
                                    <div className="w-[125px] sm:w-44 shrink-0">
                                        <select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value)}
                                            className="w-full py-2 px-2.5 text-xs font-semibold bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white outline-none cursor-pointer truncate"
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map(cat => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Filter Pills Row */}
                                <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar-blue pb-1.5 pt-0.5">
                                    {/* Selected Items Toggle Pill */}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedOnly(prev => !prev)}
                                        className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-bold transition-all flex items-center gap-1.5 select-none ${
                                            selectedOnly
                                                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                                                : 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 hover:bg-blue-100 border border-blue-200/60 dark:border-blue-800'
                                        }`}
                                    >
                                        <ShoppingCart className="w-3 h-3" />
                                        <span>Selected ({totalProductsCount})</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => { setSelectedCategory(''); setSelectedOnly(false); }}
                                        className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                            selectedCategory === '' && !selectedOnly
                                                ? 'bg-blue-600 text-white shadow-xs'
                                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                                        }`}
                                    >
                                        All
                                    </button>

                                    {categories.map(cat => (
                                        <button
                                            key={`pill-${cat}`}
                                            type="button"
                                            onClick={() => { setSelectedCategory(cat); setSelectedOnly(false); }}
                                            className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                                selectedCategory === cat && !selectedOnly
                                                    ? 'bg-blue-600 text-white shadow-xs'
                                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
                                            }`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Product List Header Info */}
                            <div className="px-1 flex items-center justify-between text-[10px] font-extrabold text-slate-400 uppercase tracking-wider relative">
                                <div className="flex items-center gap-2">
                                    <span>Showing {displayedProducts.length} {productTotalElements ? `of ${productTotalElements}` : ''} Products</span>
                                    {loadingProducts && (
                                        <div className="flex items-center gap-1 text-[10px] text-blue-600 dark:text-blue-400 font-bold lowercase animate-pulse">
                                            <div className="w-2.5 h-2.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                            <span>updating...</span>
                                        </div>
                                    )}
                                </div>
                                {selectedOnly && (
                                    <span className="text-blue-500 font-bold lowercase">Showing return items only</span>
                                )}
                            </div>
                        </div>

                        {/* Dedicated Scrollable Products List Container */}
                        <div
                            ref={scrollContainerRef}
                            className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-5 space-y-4 custom-scrollbar-blue relative"
                            onScroll={handleScroll}
                        >
                            {loadingProducts && productPage === 1 && products.length === 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
                                    {[1, 2, 3, 4, 5, 6].map(i => (
                                        <div key={i} className="p-2.5 rounded-2xl border border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between gap-2.5 animate-pulse">
                                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 shrink-0"></div>
                                                <div className="space-y-1.5 flex-1 min-w-0">
                                                    <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded-md w-3/4"></div>
                                                    <div className="h-2.5 bg-slate-100 dark:bg-slate-700/60 rounded-md w-1/2"></div>
                                                </div>
                                            </div>
                                            <div className="w-20 h-7 bg-slate-200 dark:bg-slate-700 rounded-xl shrink-0"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : displayedProducts.length === 0 ? (
                                <div className="p-8 text-center bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 rounded-2xl text-slate-400 text-xs">
                                    {selectedOnly ? 'No items selected to offload.' : 'No products match your search.'}
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
                                        {displayedProducts.map(product => {
                                            const item = getItemForProduct(product.id);
                                            const qty = item ? Number(item.qty) || 0 : 0;
                                            const isSelected = qty > 0;
                                            const currentReason = item?.reason || defaultReason;

                                            return (
                                                <div
                                                    key={product.id}
                                                    className={`p-2.5 rounded-2xl border transition-all flex items-center justify-between gap-2 ${
                                                        isSelected
                                                            ? 'bg-blue-50/70 dark:bg-blue-950/40 border-blue-400 dark:border-blue-500 ring-2 ring-blue-400/10 shadow-2xs'
                                                            : 'bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 hover:border-slate-300'
                                                    }`}
                                                >
                                                    {/* Left: Avatar & Title & Unit */}
                                                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                        <ProductAvatar
                                                            name={product.name}
                                                            image={product.image}
                                                            size="sm"
                                                            className="shrink-0"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <h4 className="text-xs font-bold text-slate-800 dark:text-white truncate">
                                                                {product.name}
                                                            </h4>
                                                            <div className="flex items-center gap-1 mt-0.5">
                                                                <span className="text-[9px] font-extrabold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded-md uppercase">
                                                                    {product.unit || 'PCS'}
                                                                </span>
                                                                <select
                                                                    value={currentReason}
                                                                    onChange={(e) => handleItemReasonChange(product, e.target.value)}
                                                                    className="px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 dark:bg-slate-700 border-0 rounded text-slate-700 dark:text-slate-200 outline-none max-w-[100px] truncate"
                                                                >
                                                                    {RETURN_REASONS.map(r => (
                                                                        <option key={r} value={r}>{r}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Right: Quantity Stepper (Same Row) */}
                                                    <div className="flex items-center gap-1 shrink-0 bg-slate-100 dark:bg-slate-700 p-1 rounded-xl border border-slate-200/80 dark:border-slate-600">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDecrement(product)}
                                                            disabled={qty === 0}
                                                            className={`w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg transition-all ${
                                                                qty === 0
                                                                    ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                                                                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 active:scale-95 shadow-2xs'
                                                            }`}
                                                        >
                                                            <Minus className="w-3.5 h-3.5" />
                                                        </button>

                                                        <input
                                                            type="number"
                                                            min="0"
                                                            value={qty || ''}
                                                            onChange={(e) => handleQtyInputChange(product, e.target.value)}
                                                            placeholder="0"
                                                            className="w-20 sm:w-24 min-w-[4.8rem] text-center font-black text-blue-600 dark:text-blue-400 text-xs bg-transparent outline-none px-1 hide-number-spinners"
                                                        />

                                                        <button
                                                            type="button"
                                                            onClick={() => handleIncrement(product)}
                                                            className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-all shadow-2xs"
                                                        >
                                                            <Plus className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Sentinel Trigger Element for IntersectionObserver */}
                                    <div ref={sentinelRef} className="h-6 w-full flex items-center justify-center my-1">
                                        {loadingProducts && productPage > 1 && (
                                            <div className="py-2 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                <span>Loading more products...</span>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {isOtherSelected && (
                                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl space-y-1">
                                    <label className="block text-xs font-bold text-amber-800 dark:text-amber-300">
                                        Specific Reason Details Required (for "Other" return selection)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Explain why item is being returned..."
                                        value={otherReasonRemarks}
                                        onChange={(e) => setOtherReasonRemarks(e.target.value)}
                                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 rounded-lg text-slate-800 dark:text-white outline-none"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Sticky POS Bottom Action Bar - Elevated with Mobile Nav Bar Clearance */}
                {!isSuccess && (
                    <div className="p-3 sm:p-4 px-4 sm:px-6 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-800 shadow-xl shrink-0 sticky bottom-0 z-30 pb-safe pb-3 sm:pb-4">
                        {isReviewing ? (
                            /* REVIEW BOTTOM BAR */
                            <>
                                <button
                                    type="button"
                                    onClick={() => setIsReviewing(false)}
                                    className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 hover:text-blue-600 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Back to Edit
                                </button>

                                <div className="flex items-center gap-2 sm:gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-3.5 sm:px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleFinalSubmit}
                                        className="px-5 sm:px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 active:scale-95 transition-all"
                                    >
                                        Confirm & Submit Request
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* CATALOG BOTTOM BAR */
                            <>
                                {/* Item Counter Badge (2-Row Layout with Smart Truncation) */}
                                <div className="flex items-center gap-2 shrink-0 min-w-0 mr-1">
                                    <span className="flex items-center justify-center min-w-[28px] h-7 bg-blue-600 text-white text-xs font-black rounded-lg px-2 shadow-xs shrink-0">
                                        {totalProductsCount}
                                    </span>
                                    <div className="flex flex-col text-[11px] leading-tight min-w-0">
                                        <span className="font-extrabold text-slate-800 dark:text-white uppercase tracking-wider truncate">
                                            {totalProductsCount} {totalProductsCount === 1 ? 'ITEM' : 'ITEMS'}
                                        </span>
                                        <span
                                            className="font-black text-blue-600 dark:text-blue-400 truncate"
                                            title={typeof totalQuantitySum === 'number' ? totalQuantitySum.toLocaleString() : totalQuantitySum}
                                        >
                                            {formatQtyDisplay(totalQuantitySum)} Total Qty
                                        </span>
                                    </div>

                                    {totalProductsCount > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleClearAll}
                                            className="p-1.5 ml-1 rounded-lg bg-rose-50 dark:bg-rose-950/40 text-rose-500 hover:bg-rose-100 transition-colors shrink-0"
                                            title="Clear Selection"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Action Buttons (Guaranteed Non-Squashing) */}
                                <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-2.5 sm:px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white shrink-0 whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleProceedToReview}
                                        className="px-4 sm:px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 active:scale-95 transition-all shrink-0 whitespace-nowrap min-w-[130px] sm:min-w-[150px] flex items-center justify-center"
                                    >
                                        Review Request ({totalProductsCount})
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CreateOffloadRequestModal;
